import { FC, useState } from 'react'
import styled, { css } from 'styled-components'
import { Box, Flex } from '@/components/atoms/Grid'
import { MediumTinyBold, Tiny2 } from '@/components/atoms/typography'
import { SingleIconProps } from '@/components/atoms/icon'
import Image from 'next/image'
import { ArrowDownIconDefault } from '../atoms/icons/ArrowDownIconDefault'
import { PromoCode } from '@/network/graphql.g'
type SelectItem = {
  id: string | number
  title: string
  iconPath?: string
  isStock?: boolean
}

enum SelectLayoutType {
  arrowAtEnd,
  arrowNextToText,
  table
}

type SelectSizesProps = {
  placeholder?: string
  selectedIndex?: number
  onSelect: (id: string | number) => void
  items: SelectItem[]
  TextElement?: JSX.Element
  SelectIcon?: (props: SingleIconProps) => JSX.Element
  layoutType?: SelectLayoutType
  showArrowDown?: boolean
  emptyValue?: string
  promoCodes: PromoCode[]
} & Rest

export const SelectSizes: FC<SelectSizesProps> = ({
  items,
  selectedIndex,
  placeholder,
  onSelect,
  SelectIcon,
  showArrowDown = true,
  TextElement = Tiny2,
  layoutType = SelectLayoutType.arrowNextToText,
  emptyValue,
  promoCodes,
  ...rest
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const item = items[selectedIndex]
  const handleSelect = (id: string | number) => {
    onSelect(id)
    setIsOpen(false)
  }
  const title = item?.title ?? placeholder ?? emptyValue
  const width = layoutType === SelectLayoutType.arrowAtEnd ? '100%' : 'auto'

  const getPromoCode = (isStock?: boolean) => {
    if (!promoCodes?.length) {
      return null
    }

    if (promoCodes.length === 1) {
      return promoCodes[0]
    }

    const sortedCodes = [...promoCodes].sort(
      (a, b) => (b.discountValue || 0) - (a.discountValue || 0)
    )

    if (isStock) {
      return sortedCodes[0]
    }

    return sortedCodes[sortedCodes.length - 1]
  }

  const selectedItemPromoCode = getPromoCode(item?.isStock)

  return (
    <SelectWrapper
      layoutType={layoutType}
      data-testid="selectWrapper"
      onClick={() => setIsOpen(!isOpen)}
      {...rest}
    >
      <Flex width={width} justifyContent="space-between">
        <Flex alignItems="center">
          {SelectIcon && <SelectIcon mr="5px" />}
          {items[selectedIndex]?.iconPath && (
            <Box mr={10}>
              <Image
                src={items[selectedIndex].iconPath}
                width={25}
                height={11}
                objectFit="contain"
                unoptimized
              />
            </Box>
          )}
          <Flex alignItems="center" gap="4px">
            <TextElement m={1}>{title}</TextElement>
            {items[selectedIndex]?.isStock && selectedItemPromoCode && (
              <StockDiscountWrapper>{`-${selectedItemPromoCode?.discountValue}%`}</StockDiscountWrapper>
            )}
          </Flex>
        </Flex>
        {showArrowDown && <ArrowDownIconDefault />}
      </Flex>
      {isOpen && (
        <DropdownList>
          {items.map(({ title, id, isStock, iconPath }) => (
            <DropdownItem key={id} onClick={() => handleSelect(id)}>
              {iconPath && (
                <Box mr={8}>
                  <Image src={iconPath} width={20} height={10} unoptimized />
                </Box>
              )}
              {title}
              {isStock && getPromoCode(isStock) && (
                <StockDiscountWrapper>
                  {`-${getPromoCode(isStock)?.discountValue}%`}
                </StockDiscountWrapper>
              )}
            </DropdownItem>
          ))}
        </DropdownList>
      )}
    </SelectWrapper>
  )
}

const TableSelectWrapperStyles = css`
  justify-content: flex-start;
  padding: 10px 23px;
`

const NormalSelectWrapperStyles = css`
  border: 1px solid ${(props) => props.theme.colors.onBackground};
  padding: 2px 13px;
  border-radius: 2px;
  justify-content: space-between;
`

type SelectWrapperProps = {
  layoutType: SelectLayoutType
}

const SelectWrapper = styled<SelectWrapperProps>(Flex)`
  flex-direction: row;
  position: relative;
  &:hover {
    cursor: pointer;
  }
  ${(props) =>
    props.layoutType === SelectLayoutType.table
      ? TableSelectWrapperStyles
      : NormalSelectWrapperStyles};
  border-radius: ${(props) => props.theme.borderRadius}px;
`

const DropdownList = styled.ul`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: ${(props) => props.theme.colors.sizeTableNeutral2};
  border: 1px solid ${(props) => props.theme.colors.sectionSeparator};
  border-radius: 6px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-top: 8px;
  padding: 4px 0;
`

const DropdownItem = styled.li`
  display: flex;
  align-items: center;
  font-size: 12px;
  justify-content: space-between;
  padding: 6px 12px;
  color: ${(props) => props.theme.colors.onBackground};
  &:hover {
    background-color: ${(props) => props.theme.colors.neutralBorder};
  }
`

const StockDiscountWrapper = styled(MediumTinyBold)`
  background-color: ${(props) => props.theme.colors.accent};
  color: ${(props) => props.theme.colors.background};
  padding: 1px 2px;
  margin: 0;
`
